import React, { Suspense, useCallback, useContext, useEffect, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { AuthContext } from '../../Context/ContextProvider';
import { get_my_jobs_list } from '../../Services/Student/StudentApi';
import { get_public_job_lead } from '../../Services/Website/WebsiteApi';
import '../../StudentPanel/Dashboard/JobDashboard.css';
import './JobLead.css';

const RightPanel = React.lazy(() => import('../../StudentPanel/Dashboard/RightPanel'));
const Sidebar = React.lazy(() => import('../../StudentPanel/Dashboard/SideBar'));
const wait = ms => new Promise(resolve => setTimeout(resolve, ms));

const JobLead = () => {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { isLoggedIn, userRole, jobs, savedCount, appliedCount, setActiveTab, setSavedCount, setAppliedCount, setJobs } =
    useContext(AuthContext);
  const leadId = searchParams.get('id');
  const emailToken = searchParams.get('token');
  const [activeSection, setActiveSection] = useState(null);

  const [loading, setLoading] = useState(true);
  const [errorState, setErrorState] = useState(null);
  const [jobLeadData, setJobLeadData] = useState(null);
  const showSidebar = isLoggedIn && (userRole === 'student_registered' || userRole === 'seeker');
  const isPreviewRole = isLoggedIn && userRole !== 'student_registered' && userRole !== 'seeker';
  const dashboardPath = userRole === 'seeker' ? '/seeker-dashboard' : '/student-dashboard';

  const fetchJobLead = useCallback(async () => {
    if (!leadId && !emailToken) {
      setErrorState({
        title: 'This link is invalid.',
        message: 'This link is invalid.',
        canRetry: false,
      });
      setLoading(false);
      return;
    }

    setLoading(true);
    setErrorState(null);

    try {
      let response;
      let lastError;

      for (let attempt = 0; attempt < 3; attempt += 1) {
        try {
          response = await get_public_job_lead({ id: leadId, token: emailToken });
          break;
        } catch (error) {
          lastError = error;
          const status = error?.response?.status;
          const shouldRetry = !status || status >= 500;

          if (!shouldRetry || attempt === 2) {
            throw error;
          }

          await wait(500);
        }
      }

      if (!response) {
        throw lastError;
      }

      setJobLeadData(response);
    } catch (error) {
      const status = error?.response?.status;

      if (status === 400) {
        setErrorState({
          title: 'This link is invalid.',
          message: 'This link is invalid.',
          canRetry: false,
        });
      } else if (status === 404) {
        setErrorState({
          title: 'This job is no longer available.',
          message: 'This job is no longer available.',
          canRetry: false,
        });
      } else {
        setErrorState({
          title: 'Something went wrong',
          message: 'We could not load this job right now. Please try again.',
          canRetry: true,
        });
      }
    } finally {
      setLoading(false);
    }
  }, [emailToken, leadId]);

  const refreshSidebarCounts = useCallback(async () => {
    if (!showSidebar) return;

    try {
      const recommendedResponse = await get_my_jobs_list();
      setJobs(recommendedResponse?.jobs?.fresh_jobs || []);
      setSavedCount(recommendedResponse?.jobs?.saved_jobs_count || 0);
      setAppliedCount(recommendedResponse?.jobs?.applied_jobs_count || 0);
    } catch (error) {
      console.error('Failed to bootstrap sidebar counts:', error);
    }
  }, [setAppliedCount, setJobs, setSavedCount, showSidebar]);

  useEffect(() => {
    fetchJobLead();
  }, [fetchJobLead]);

  useEffect(() => {
    refreshSidebarCounts();
  }, [refreshSidebarCounts]);

  const job = jobLeadData?.job;
  const viewer = jobLeadData?.viewer || {};
  const loginRedirect = `${location.pathname}${location.search}`;

  const handleJobSaveStatus = (jobId, isSaved) => {
    setJobLeadData(prev => {
      if (!prev?.job || prev.job.view_id !== jobId) return prev;
      return {
        ...prev,
        job: {
          ...prev.job,
          time_saved: isSaved ? new Date() : null,
        },
      };
    });
    setSavedCount(prev => Math.max(0, (prev || 0) + (isSaved ? 1 : -1)));
  };

  const handleJobApplied = async () => {
    await Promise.all([fetchJobLead(), refreshSidebarCounts()]);
  };

  const handleSidebarTabChange = tab => {
    setActiveSection(tab);
    setActiveTab(tab);
    navigate(dashboardPath);
  };

  const handleLoginRedirect = () => {
    navigate(`/login?redirect=${encodeURIComponent(loginRedirect)}`);
  };

  if (loading) {
    return (
      <div className="job-lead-page d-flex align-items-center justify-content-center">
        <div className="text-center">
          <div className="spinner-border text-primary" role="status" />
          <p className="mt-3 mb-0">Loading job details...</p>
        </div>
      </div>
    );
  }

  if (errorState) {
    return (
      <div className="job-lead-page d-flex align-items-center justify-content-center">
        <div className="card job-lead-error-card text-center">
          <h2 className="mb-3">{errorState.title}</h2>
          <p className="text-muted mb-0">{errorState.message}</p>
          {errorState.canRetry && (
            <button type="button" className="apply job-lead-retry-btn mt-3" onClick={fetchJobLead}>
              Retry
            </button>
          )}
        </div>
      </div>
    );
  }

  if (!job) {
    return (
      <div className="job-lead-page d-flex align-items-center justify-content-center">
        <div className="card job-lead-error-card text-center">
          <h2 className="mb-3">This job is no longer available.</h2>
          <p className="text-muted mb-0">This job is no longer available.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="job-lead-page">
      {!isLoggedIn && (
        <header className="job-lead-public-header">
          <button type="button" className="job-lead-public-logo" onClick={() => navigate('/')}>
            <img src={`${process.env.PUBLIC_URL}/assets/images/logo/SeekJobsLog1.png`} alt="SeekJobs" />
          </button>
          <button type="button" className="job-lead-public-login" onClick={handleLoginRedirect}>
            Log In
          </button>
        </header>
      )}
      <div className="job-lead-layout">
        <div className="job-lead-shell">
          {showSidebar && (
            <div className="job-lead-sidebar d-lg-block d-none">
              <Suspense fallback={<div className="job-lead-sidebar-loader" />}>
                <Sidebar
                  activeSection={activeSection}
                  setActiveSection={setActiveSection}
                  recommendedCount={jobs?.length || 0}
                  savedCount={savedCount}
                  appliedCount={appliedCount}
                  handleChangeTab={handleSidebarTabChange}
                />
              </Suspense>
            </div>
          )}

          <div className="job-lead-content">
            {isPreviewRole ? (
              <div className="job-lead-login-note">This is a preview of the job lead as it will be presented to the recipient.</div>
            ) : (
              !viewer?.authenticated && <div className="job-lead-login-note">Log in to see your personalized match score.</div>
            )}
            {!showSidebar && (
              <div
                style={{
                  backgroundImage: `url(${process.env.PUBLIC_URL}/assets/images/section-image.png), linear-gradient(15deg, rgba(53, 136, 233), rgba(37, 91, 153))`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  backgroundRepeat: 'no-repeat',
                  color: 'white',
                }}
                className="insider-card job-lead-public-insider"
              >
                <div className="insider-text">
                  <h4>Insider Network</h4>
                  <p>Discover connections within this company.</p>
                </div>
                <button
                  className="explore-btn"
                  disabled={isPreviewRole}
                  style={isPreviewRole ? { opacity: 1, cursor: 'not-allowed', pointerEvents: 'none' } : undefined}
                  onClick={handleLoginRedirect}
                >
                  <span className="icon">
                    <img src={`${process.env.PUBLIC_URL}/assets/images/SvgIcon/abstract.svg`} alt="" />
                  </span>{' '}
                  Explore
                </button>
              </div>
            )}
            <Suspense
              fallback={
                <div className="job-lead-panel-loader text-center">
                  <div className="spinner-border text-primary" role="status" />
                </div>
              }
            >
              <RightPanel
                job={job}
                style={{ height: 'auto', minHeight: 'calc(100vh - 40px)' }}
                onJobApplied={handleJobApplied}
                updateJobSaveStatus={handleJobSaveStatus}
                requireAuthForActions
                actionLoginRedirect={loginRedirect}
                hideInsiderNetwork={!showSidebar}
                hideScore={isPreviewRole || !viewer?.authenticated || job?.rating === null || job?.rating === undefined}
                disableActions={isPreviewRole}
                className="job-lead-detail-card"
              />
            </Suspense>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JobLead;
